import { useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ResourceContext } from '../contexts/ResourceContext';
import { SoundList } from '../models/Voice';
import {
    getSettingSoundState,
    getSettingVolumeState,
} from '../modules/setting/selector';

const useAudioPlay = () => {
    const volume = useRef<number>(0);
    const [playing, setPlaying] = useState<boolean>(false);

    const { audioLists } = useContext(ResourceContext);
    const { Game, Mute } = useSelector(getSettingVolumeState);
    const { Voice2 } = useSelector(getSettingSoundState);

    const [muted, setMuted] = useState<boolean>(false);
    const [audioName, setAudioName] = useState<string>('');
    const [urlMap, setUrlMap] = useState<Map<string, string>>(new Map());
    const [indexMap, setIndexMap] = useState<Map<string, number>>(new Map());
    const loadWebAudio = async (name: string, url: string): Promise<number> => {
        return new Promise<number>(resolve => {
            if (!urlMap.has(name)) {
                setUrlMap(urlMap.set(name, url));
                webaudio.load(
                    url,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (_url: string, _soundBuffer: any, idx: number) => {
                        setIndexMap(indexMap.set(name, idx));
                        resolve(idx);
                    }
                );
            }
        });
    };
    const play = async (name: string, loop?: number): Promise<void> => {
        if (loop == undefined) loop = 1;
        // eslint-disable-next-line @typescript-eslint/ban-types
        const executor = (resolve: Function, reject: Function) => {
            if (loop == undefined) loop = 1;
            const isLoop = loop <= 0;
            const url = urlMap.get(name);
            console.log('app:sound:play', name);
            if (url == undefined) {
                const url = audioLists.get(name);
                if (url) {
                    loadWebAudio(name, url).then((idx: number) => {
                        webaudio.playIndex(idx, isLoop, resolve);
                        webaudio.setVolumnIndex(idx, volume.current);
                        setAudioName(name);
                    });
                } else {
                    reject();
                }
            } else {
                const index = indexMap.get(name);
                if (index == undefined) {
                    console.error('app:sound:undefined index');
                } else {
                    webaudio.stopIndex(index);
                    webaudio.playIndex(index, isLoop, resolve);
                    webaudio.setVolumnIndex(index, volume.current);
                }
                setAudioName(name);
            }
        };
        return new Promise<void>(executor);
    };
    const audioPlay = async (key: string | string[]) => {
        // console.log('app:sound:function', 'audioPlay:volume', volume);
        if (volume.current > 0 && !muted) {
            if (typeof key === 'string') {
                play(key).then(() => verifyAllSourcesStopped());
            } else {
                for await (const k of key) {
                    await play(k);
                }
                await verifyAllSourcesStopped();
            }
        }
    };

    const verifyAllSourcesStopped = () => {
        const stopped = webaudio.allSourceStopped();
        setPlaying(!stopped);
    };
    const audioStop = () => {
        if (indexMap.has(audioName)) {
            webaudio.stopIndex(indexMap.get(audioName));
        }
    };
    const setMasterVolumn = (val: number) => {
        webaudio.setMasterVolumn(val);
    };
    const setAudioVolume = (val: number) => {
        if (indexMap.has(audioName)) {
            webaudio.setVolumnIndex(indexMap.get(audioName) as number, val);
        }
    };
    const mute = (): void => {
        setMuted(true);
        if (indexMap.has(audioName)) {
            webaudio.setVolumnIndex(indexMap.get(audioName) as number, 0);
        }
    };

    const unmute = (): void => {
        setMuted(false);
        if (indexMap.has(audioName)) {
            webaudio.setVolumnIndex(
                indexMap.get(audioName) as number,
                volume.current
            );
        }
    };
    const setTestGameVolumeAudio = (vol: number): void => {
        volume.current = vol / 100;
    };
    const playButtonAudio = (): void => {
        audioPlay(SoundList.btnSound);
    };
    const playChipBetAudio = (): void => {
        audioPlay(SoundList.ChipsBet);
    };
    const handleVisibilitychange = () => {
        if (document.visibilityState === 'visible') {
            setMasterVolumn(volume.current);
            unmute();
        } else {
            setMasterVolumn(0);
            mute();
        }
    };

    /**
     * Neo audio control logic
     */
    useEffect(() => {
        volume.current = Mute ? 0 : Game / 100;
    }, [Game, Mute]);
    useEffect(() => {
        setAudioVolume(volume.current);
        setMasterVolumn(volume.current);
    }, [volume.current]);

    useEffect(() => {
        window.addEventListener('visibilitychange', handleVisibilitychange);
        return () => {
            window.removeEventListener(
                'visibilitychange',
                handleVisibilitychange
            );
        };
    }, [audioName]);
    useEffect(() => {
        // console.log('app:sound:playing:', playing);
        //set delay init if sound is playing
        if (playing) {
            console.log('app:sound:delay initialize since playing sound');
        } else {
            //set init
            console.log('app:sound:reset');
            // setMasterVolumn(0);
            setUrlMap(new Map());
            setIndexMap(new Map());
        }
    }, [Voice2, playing]);
    return {
        audioPlay,
        audioStop,
        mute,
        unmute,
        setTestGameVolumeAudio,
        playButtonAudio,
        playChipBetAudio,
        setMasterVolumn,
    };
};

export default useAudioPlay;
export type useAudioPlayState = ReturnType<typeof useAudioPlay>;
